import type { ColumnId, PublicRetroRoom } from '@planning-session/shared';
import { Column } from './Column.js';
import styles from './Board.module.css';

export interface BoardProps {
  readonly room: PublicRetroRoom;
  readonly isPlayer: boolean;
  readonly isHost: boolean;
  onAddNote: (column: ColumnId, text: string) => void;
  onEditNote: (noteId: string, text: string) => void;
  onRemoveNote: (noteId: string) => void;
  onVoteNote: (noteId: string) => void;
  onUnvoteNote: (noteId: string) => void;
  onCombineNotes: (noteId: string, intoNoteId: string) => void;
  onUnmergeNote: (noteId: string) => void;
}

// Decorative, not a state colour — the one named exception to the State-Only
// Rule for this surface. See DESIGN.md's "The Column Accent Rule."
const COLUMNS: readonly { readonly id: ColumnId; readonly heading: string; readonly accent: 'green' | 'amber' | 'indigo' }[] = [
  { id: 'wentWell', heading: 'Went well', accent: 'green' },
  { id: 'toImprove', heading: 'To improve', accent: 'amber' },
  { id: 'actionItems', heading: 'Action items', accent: 'indigo' },
];

export function Board({ room, isPlayer, isHost, onAddNote, onEditNote, onRemoveNote, onVoteNote, onUnvoteNote, onCombineNotes, onUnmergeNote }: BoardProps) {
  const concealing = room.mode === 'anonymous' && room.phase === 'writing';

  return (
    <div className={styles.board}>
      {COLUMNS.map(({ id, heading, accent }, columnIndex) => {
        const locked = id === 'actionItems' ? room.phase === 'writing' : room.phase === 'revealed';
        return (
          <Column
            key={id}
            heading={heading}
            accent={accent}
            column={id}
            columnIndex={columnIndex}
            notes={room.notes.filter((note) => note.column === id)}
            phase={room.phase}
            concealing={concealing}
            locked={locked}
            isPlayer={isPlayer}
            isHost={isHost}
            viewerId={room.you?.id ?? null}
            onAddNote={(text) => onAddNote(id, text)}
            onEditNote={onEditNote}
            onRemoveNote={onRemoveNote}
            onVoteNote={onVoteNote}
            onUnvoteNote={onUnvoteNote}
            onCombineNotes={onCombineNotes}
            onUnmergeNote={onUnmergeNote}
          />
        );
      })}
    </div>
  );
}
